/**
 * useUserPreference — server-persisted per-user UI preferences.
 *
 * Backed by users.preferences (JSONB) via GET/PATCH /api/users/me/preferences —
 * NOT localStorage, so preferences follow the user across browsers/devices.
 *
 * Auth discipline (2026-07-22): the load WAITS on apiService.whenAuthReady()
 * instead of racing the boot-time auth refresh (the race guaranteed a 401 on
 * every cold boot and silently discarded the user's saved preferences for the
 * whole session). AuthContext calls reloadUserPreferences() on login/refresh
 * and resetUserPreferences() on logout; EmbedAuthProvider resolves the
 * barrier synchronously, so embed consumers load immediately.
 *
 * Module-level cache: one fetch per auth session shared by all consumers;
 * setValue is optimistic (local update + fire-and-forget PATCH).
 */
import { useCallback, useEffect, useState } from 'react';
import { apiService } from '../services/ApiService';
import { logger } from '../utils/log';

type Prefs = Record<string, unknown>;

let cache: Prefs | null = null;
/** The last load reached the server. A failed load falls back to defaults but is not "ready". */
let loadedOk = false;
let inflight: Promise<void> | null = null;
let localOnly = false;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());

const loadPrefs = (): void => {
  if (localOnly || cache || inflight) return;
  inflight = (async () => {
    try {
      await apiService.whenAuthReady();
      const response: any = await apiService.get('/users/me/preferences');
      cache = (response && typeof response.preferences === 'object' && response.preferences) || {};
      loadedOk = true;
    } catch (err: any) {
      logger.debug('[useUserPreference] load failed (defaults apply):', err?.message);
      cache = {};
    } finally {
      inflight = null;
      notify();
    }
  })();
};

/**
 * AuthContext, after login/refresh succeeds: fetch fresh preferences.
 * If a load is already pending (it was parked on the auth barrier and has just
 * been released), let it complete — it now carries the fresh credentials.
 */
export const reloadUserPreferences = (): void => {
  if (inflight) return;
  cache = null;
  loadedOk = false;
  loadPrefs();
};

/** AuthContext, on logout: drop to defaults and re-arm for the next session. */
export const resetUserPreferences = (): void => {
  cache = null;
  loadedOk = false;
  notify();
};

/**
 * The partner embed has no user to own preferences (and its token may not reach
 * /users/me/preferences): defaults apply, changes live in memory only, nothing is fetched or saved.
 * Called during EmbedAuthProvider's first render, before any reader mounts — so no notify().
 */
export const keepUserPreferencesLocal = (): void => {
  localOnly = true;
  cache = cache || {};
  loadedOk = true;
};

/** Test-only alias. */
export const _resetUserPreferenceCache = (): void => {
  localOnly = false;
  resetUserPreferences();
};

/**
 * [value, setValue, ready] — `ready` is true once this person's saved preferences have actually
 * loaded, so "not set" can be told apart from "not known yet" (e.g. a notice shown only once).
 */
export function useUserPreference<T>(key: string, defaultValue: T): [T, (next: T) => void, boolean] {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const listener = () => forceRender((n) => n + 1);
    listeners.add(listener);
    loadPrefs();
    return () => { listeners.delete(listener); };
  }, []);

  const value = (cache && key in cache ? (cache as Prefs)[key] : defaultValue) as T;

  const setValue = useCallback((next: T) => {
    cache = { ...(cache || {}), [key]: next };
    notify();
    if (localOnly) return;
    apiService.patch('/users/me/preferences', { [key]: next }).catch((err: any) => {
      // Keep the optimistic value — the next session's load reconciles.
      logger.debug('[useUserPreference] persist failed:', err?.message);
    });
  }, [key]);

  return [value, setValue, cache !== null && loadedOk];
}
